import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
// Serif and mono faces are not self-hosted yet — the packages are deliberately
// not installed. The font stacks in the tokens stay on the new implementation
// and fall back to Georgia / ui-monospace until these are restored.
// import "@fontsource/source-serif-4/400.css";
// import "@fontsource/source-serif-4/500.css";
// import "@fontsource/source-serif-4/600.css";
// import "@fontsource/jetbrains-mono/400.css";
// import "@fontsource/jetbrains-mono/500.css";
// import "@fontsource/jetbrains-mono/600.css";
import { createTheme } from "@mui/material";
import type { Shadows } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { BUTTON_BASE_SX } from "./components/app/StyledButtonV2";
import { COMPONENT, PRIMITIVE } from "./tokens/design-tokens";
import {
  ECARE_COLOR,
  ECARE_LETTER_SPACING,
  ECARE_RADIUS,
  ECARE_SHADOW,
} from "./tokens/ecare-tokens";

const p = PRIMITIVE;
const c = p.color;
/** Fields follow the styleguide's small field (§8 `.input-sm`), not the 40px standard one. */
export const INPUT_FIELD_HEIGHT = "32px";
const INPUT_PADDING_X = 10;
const INPUT_FONT_SIZE = p.typography.fontSize.body.b4;

/** §8 states this one in raw pixels; it is deliberately off the 6/9/12 radius scale. */
const INPUT_RADIUS = 8;

/** Shared radius for buttons and dropdowns (§4). Fields use `INPUT_RADIUS`. */
export const CONTROL_RADIUS = ECARE_RADIUS.control;

/** Every outline state keeps the same 1px stroke; only the colour changes. */
export const INPUT_OUTLINE_WIDTH = 1;

/** Floating label metrics — the shrunk label and the notch legend share them. */
const INPUT_LABEL_FONT_SIZE = p.typography.fontSize.body.b6;
const INPUT_LABEL_SHRINK_SCALE = 0.75;

/**
 * Legend text inset, measured from the fieldset's own 8px padding so the notch
 * opens exactly where MUI floats the shrunk label (`translate(14px, …)`).
 */
const LEGEND_INSET = 6;

/**
 * A field with no label still gets a legend — MUI fills it with a zero-width
 * space and, once the field is filled or focused, notches the outline for it.
 * That leaves a gap in the top border with nothing in it, so close it.
 */
const UNLABELLED_LEGEND = "& legend:has(> span.notranslate)";

const UNLABELLED_LEGEND_STYLE = {
  width: 0,
  maxWidth: 0,
  padding: 0,
  "& > span": {
    paddingInline: 0,
  },
};

/**
 * The outline's notch is cut to the legend's own text, so it clears the label
 * only when both render with the same metrics — MUI's lighter, untracked
 * default measures short and lets the stroke run into the label.
 */
const NOTCHED_OUTLINE_STYLE = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: COMPONENT.inputField.outline.default,
    "& legend": {
      fontSize: `${INPUT_LABEL_FONT_SIZE * INPUT_LABEL_SHRINK_SCALE}px`,
      fontWeight: p.typography.fontWeight.semiBold,
      letterSpacing: ECARE_LETTER_SPACING.overline,
      "& span": {
        paddingInline: `${LEGEND_INSET}px`,
      },
    },
    [UNLABELLED_LEGEND]: UNLABELLED_LEGEND_STYLE,
  },
};

/**
 * Focus state (§8). Colour only — MUI thickens the focused outline to 2px on
 * its own, which reads as a heavy ring next to the notched label.
 */
const FOCUS_RING_STYLE = {
  backgroundColor: COMPONENT.inputField.bg.selected,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: ECARE_COLOR.border.focus,
    borderWidth: `${INPUT_OUTLINE_WIDTH}px`,
  },
};

/** MUI widens a focused outline to 2px, error included; hold it at 1px. */
const ERROR_OUTLINE_STYLE = {
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: COMPONENT.inputField.outline.error,
    borderWidth: `${INPUT_OUTLINE_WIDTH}px`,
  },
};

/**
 * Fields across the app force `InputLabelProps={{ shrink: true }}`, but MUI
 * opens the notch only once the input is filled, focused or start-adorned — so
 * an empty resting field floats its label onto an unbroken outline. Open the
 * notch from the label's own shrunk state instead.
 */
const FORCED_SHRINK_NOTCH_STYLE = {
  "&:has(> .MuiInputLabel-outlined.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend:not(:has(> span.notranslate))":
    {
      maxWidth: "100%",
    },
};

/** Left inset of a field's value text — the horizontal padding on the input slot. */
const FIELD_TEXT_OFFSET = INPUT_PADDING_X;

/**
 * Date/time pickers hold the small-field height and 14px value text like every
 * other field. MUI X v9 renders the value through the accessible section field
 * (`MuiPickersInputBase` / `MuiPickersSectionList`), so the standard
 * `MuiOutlinedInput-*` overrides never reach it — style both class families.
 */
const PICKER_FIELD_SX = {
  "& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root, & .MuiPickersInputBase-root":
    {
      height: INPUT_FIELD_HEIGHT,
      minHeight: INPUT_FIELD_HEIGHT,
      fontSize: `${INPUT_FONT_SIZE}px`,
      borderRadius: `${INPUT_RADIUS}px`,
      padding: `0 ${INPUT_PADDING_X}px`,
    },
  "& .MuiOutlinedInput-input, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root":
    {
      padding: "0 !important",
      fontSize: `${INPUT_FONT_SIZE}px`,
    },
  // The visible digits render in the section spans, which carry their own size.
  "& .MuiPickersInputBase-input, & .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionContent":
    {
      fontSize: `${INPUT_FONT_SIZE}px`,
      lineHeight: 1.4375,
    },
  // Outline carries the field's squircle radius and the default hairline.
  "& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline":
    {
      borderRadius: `${INPUT_RADIUS}px`,
      borderColor: COMPONENT.inputField.outline.default,
    },
  "& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline":
    {
      borderColor: COMPONENT.inputField.outline.hover,
    },
  // Hold the outline at 1px on focus/error — MUI widens it to 2px on its own,
  // which reads as a heavy border next to the 1px sibling fields.
  "& .MuiPickersOutlinedInput-root.Mui-focused": {
    backgroundColor: COMPONENT.inputField.bg.selected,
    "& .MuiPickersOutlinedInput-notchedOutline": {
      borderColor: ECARE_COLOR.border.focus,
      borderWidth: `${INPUT_OUTLINE_WIDTH}px`,
    },
  },
  "& .MuiPickersOutlinedInput-root.Mui-error .MuiPickersOutlinedInput-notchedOutline":
    {
      borderColor: COMPONENT.inputField.outline.error,
      borderWidth: `${INPUT_OUTLINE_WIDTH}px`,
    },
  // Calendar/clock adornment icon sized to the 32px field.
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    fontSize: "18px",
  },
};

/**
 * A field rendered `readOnly`: a value being presented rather than captured.
 *
 * Which fields those are is decided in `components/ReadOnlyFields` — a preview
 * screen wraps itself once and the common field components render `readOnly`
 * instead of `disabled` inside it. This rule is the other half: the look.
 *
 * Matched from the `MuiFormControl` root so the field's label, value, outline
 * and adornments can all be reached, and deliberately narrow:
 *
 * - `> .MuiOutlinedInput-root` — only the outlined variant, so borderless
 *   `standard` fields keep their own look.
 * - `> input` / `> textarea` — only real text inputs, so controls that use
 *   `readOnly` to block typing while staying interactive keep their styling.
 *   The multi-select trigger renders `inputComponent: "div"` and is excluded
 *   by the tag on purpose.
 *
 * `disabled` is untouched and keeps the theme's grey treatment, so locked
 * fields on editable forms still read as locked.
 */
const READ_ONLY_FIELD = [
  "&:has(> .MuiOutlinedInput-root > input[readonly])",
  // A multiline field always renders a second, `aria-hidden` textarea that MUI
  // measures the row height against, and that mirror is hardcoded `readonly`.
  // Matching it would present every editable text area — stripping its border
  // and hiding its placeholder — so only the real input counts.
  "&:has(> .MuiOutlinedInput-root > textarea[readonly]:not([aria-hidden]))",
  // Opt-in for controls whose value slot is not a real input — the multi-select
  // trigger renders a div, so it marks itself rather than carrying `readonly`.
  '&:has(> .MuiOutlinedInput-root > [data-readonly="true"])',
].join(", ");

/**
 * Standardised non-editable look: brighter background fill, no border/stroke,
 * and the standard application text colours for both the value and the label.
 */
const READ_ONLY_FIELD_STYLE = {
  // Brighter fill, held through focus: a read-only field is still focusable
  // and would otherwise pick up the focused (selected) fill.
  "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: COMPONENT.inputField.bg.default,
  },
  // No stroke, in any interaction state.
  "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: "transparent",
    },
  "& .MuiInputBase-input": {
    color: COMPONENT.inputField.text.label,
    WebkitTextFillColor: COMPONENT.inputField.text.label,
  },
  // An empty read-only field has nothing to prompt for — a placeholder reads as
  // an invitation to type into something that cannot be typed into. Leave the
  // field blank instead.
  //
  // MUI styles placeholders through vendor-prefixed pseudo-elements only, and
  // re-shows them on `:focus` — which now applies, because a read-only field is
  // still focusable. Each prefix needs its own rule: a selector list containing
  // one pseudo-element the engine doesn't know invalidates the whole rule.
  ...Object.fromEntries(
    [
      "::placeholder",
      "::-webkit-input-placeholder",
      "::-moz-placeholder",
      ":-ms-input-placeholder",
      "::-ms-input-placeholder",
    ].flatMap((ph) => [
      [`& .MuiInputBase-input${ph}`, { opacity: 0 }],
      [`& .MuiInputBase-input:focus${ph}`, { opacity: 0 }],
    ])
  ),
  "& .MuiInputLabel-root": {
    color: COMPONENT.inputField.text.label,
  },
  // Values start 8px in, while MUI floats the shrunk outlined label at 14px.
  // Pull the label back so label and value share a starting position.
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: `translate(${FIELD_TEXT_OFFSET}px, -9px) scale(0.75)`,
  },
  // A picker's calendar button is inert on a read-only field and would push
  // the date value out of line with its label.
  "& .MuiInputAdornment-positionStart .MuiIconButton-root": {
    display: "none",
  },
};

const theme = createTheme({
  typography: {
    fontFamily: p.typography.fontFamily,
    // Serif is reserved for the "moments" — H1, H2 and page titles (§2.1).
    h1: {
      fontFamily: p.typography.fontFamilySerif,
      fontWeight: p.typography.fontWeight.medium,
      letterSpacing: ECARE_LETTER_SPACING.display,
    },
    h2: {
      fontFamily: p.typography.fontFamilySerif,
      fontWeight: p.typography.fontWeight.medium,
      letterSpacing: ECARE_LETTER_SPACING.display,
    },
  },
  palette: {
    primary: {
      main: c.brandBlue[700], // #0E4486 — Brand
      light: c.brandBlue[50], // #E4ECF5 — accent-soft
      dark: c.brandBlue[900], // #0A2F5C — pressed
      contrastText: c.neutrals["000"],
    },
    secondary: {
      main: c.darkBlue[600], // #2B4D76 — accent-soft-text
      light: c.brandBlue[50], // #E4ECF5
      dark: c.brandBlue[800], // #0C386F
      contrastText: c.neutrals["000"],
    },
    error: {
      main: c.red[500], // #B33A2C
      light: c.red[50], // #F7E0DC
      dark: c.red[700], // #7C281E
      contrastText: c.neutrals["000"],
    },
    warning: {
      main: c.orange[500], // #C77A2C
      light: c.orange[50], // #FAEED9
      dark: c.orange[700], // #8A521E
      contrastText: c.neutrals["000"],
    },
    success: {
      main: c.green[500], // #3F8F5C
      light: c.green[50], // #E6F1EA
      dark: c.green[700], // #2B613E
      contrastText: c.neutrals["000"],
    },
    info: {
      main: c.brandBlue[700], // #0E4486
      light: c.brandBlue[50], // #E4ECF5
      dark: c.brandBlue[800], // #0C386F
      contrastText: c.neutrals["000"],
    },
    grey: {
      50: c.neutrals[200],
      100: c.neutrals[400],
      200: c.neutrals[700],
      300: c.neutrals[1000],
      400: c.neutrals[1200],
      500: c.neutrals[1700],
      600: c.neutrals[2000],
      700: c.neutrals[2200],
      800: c.neutrals[2400],
      900: c.neutrals[2600],
    },
    background: {
      default: c.neutrals[400], // #F7F9FC — page canvas
      paper: c.neutrals["000"], // #FFFFFF — cards, panels, modals
    },
    text: {
      primary: c.neutrals[2400], // #1C2330
      secondary: c.neutrals[2200], // #46505F
      disabled: c.neutrals[1600], // #AEB6C2
    },
    divider: c.neutrals[800], // #EAEEF6
  },
  // Resting surfaces carry no shadow — only a hairline border. Shadows signal
  // something floating (§5).
  shadows: [
    "none",
    ECARE_SHADOW.xs,
    ECARE_SHADOW.sm,
    ECARE_SHADOW.sm,
    ECARE_SHADOW.md,
    ECARE_SHADOW.md,
    ECARE_SHADOW.md,
    ECARE_SHADOW.lg,
    ECARE_SHADOW.lg,
    ...(Array(16).fill(ECARE_SHADOW.lg) as string[]),
    ECARE_SHADOW.xl,
  ] as unknown as Shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1536,
    },
  },
  components: {
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            height: INPUT_FIELD_HEIGHT,
            minHeight: INPUT_FIELD_HEIGHT,
            padding: `0 ${INPUT_PADDING_X}px`,
            fontSize: `${INPUT_FONT_SIZE}px`,
          },
          "& .MuiAutocomplete-input": {
            padding: "0 !important",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        sx: BUTTON_BASE_SX,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          // Base standard style for button with any variant
          ...(BUTTON_BASE_SX as any),
          textTransform: "none" as const,
          fontSize: `${p.typography.fontSize.body.b4}px`,
          fontWeight: p.typography.fontWeight.medium,
          letterSpacing: ECARE_LETTER_SPACING.body,
          borderRadius: COMPONENT.button.primary.radius.default,
          boxShadow: "none",
          transition: "background-color 120ms, border-color 120ms, color 120ms",
          "&:active": { transform: "translateY(1px)" },
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 3px ${ECARE_COLOR.border.focus}`,
          },
          ...(ownerState.variant === "contained" && {
            backgroundColor: COMPONENT.button.primary.bg.default,
            boxShadow: "none",
            ":hover": {
              backgroundColor: COMPONENT.button.primary.bg.hover,
              boxShadow: "none",
            },
            ":active": {
              backgroundColor: COMPONENT.button.primary.bg.pressed,
            },
          }),
          ...(ownerState.variant === "outlined" && {
            borderColor: COMPONENT.button.secondary.outline.default,
            color: COMPONENT.button.secondary.text.default,
            ":hover": {
              borderColor: COMPONENT.button.secondary.outline.hover,
              backgroundColor: COMPONENT.button.secondary.bg.hover,
              color: COMPONENT.button.secondary.text.hover,
            },
          }),
          ...(ownerState.variant === "text" && {
            color: COMPONENT.button.tertiary.text.default,
            ":hover": {
              color: COMPONENT.button.tertiary.text.hover,
            },
          }),
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "&, [class^=MuiDataGrid]": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
            outline: "none !important",
            backgroundColor: COMPONENT.table.bg.header,
          },
          "& .MuiDataGrid-virtualScroller": {
            marginTop: "0 !important",
          },
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: COMPONENT.table.bg.row1,
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: COMPONENT.table.bg.row2,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: `${p.typography.fontSize.body.b5}px`,
            fontWeight: p.typography.fontWeight.semiBold,
            letterSpacing: ECARE_LETTER_SPACING.overline,
            textTransform: "uppercase",
            color: ECARE_COLOR.text.tertiary,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${ECARE_COLOR.border.divider}`,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: ECARE_COLOR.bg.hover,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: `${INPUT_RADIUS}px`,
          height: INPUT_FIELD_HEIGHT,
          "& .MuiSelect-select": {
            padding: `0 ${INPUT_PADDING_X}px`,
            fontSize: `${INPUT_FONT_SIZE}px`,
            display: "flex",
            alignItems: "center",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: "10px",
          marginRight: "6px",
          width: 32,
          height: 16,
          padding: 0,
          backgroundColor: "transparent",
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2px",
            transitionDuration: "300ms",
            color: "#08345A",
            "&:hover": { backgroundColor: "transparent" },
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: "#08345A",
              "& + .MuiSwitch-track": {
                backgroundColor: "#AEDAFF !important",
                opacity: 1,
                border: "2px solid #AEDAFF",
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.2,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: c.green[400],
              border: `6px solid ${c.neutrals["000"]}`,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color:
                theme.palette.mode === "light"
                  ? theme.palette.grey[100]
                  : theme.palette.grey[600],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 12,
            height: 12,
          },
          "& .MuiSwitch-track": {
            borderRadius: 8,
            backgroundColor: "#fff !important",
            border: "1px solid #7A7A7A",
            opacity: "1 !important",
            height: "100%",
            boxSizing: "border-box",
            transition: theme.transitions.create(["background-color"], {
              duration: 100,
            }),
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          ...FORCED_SHRINK_NOTCH_STYLE,
          [READ_ONLY_FIELD]: READ_ONLY_FIELD_STYLE,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: `${INPUT_LABEL_FONT_SIZE}px`,
          lineHeight: "14px",
          fontWeight: p.typography.fontWeight.semiBold,
          letterSpacing: ECARE_LETTER_SPACING.overline,
          color: ECARE_COLOR.text.tertiary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: ownerState?.style?.height ?? INPUT_FIELD_HEIGHT,
          // Single-line fields hold the small-field height (§8); an adornment
          // button (date/time pickers) would otherwise stretch the row past it.
          // Multiline textareas keep their auto height.
          ...(!ownerState?.multiline && {
            height: ownerState?.style?.height ?? INPUT_FIELD_HEIGHT,
          }),
          display: "flex",
          alignItems: "center",
          borderRadius: `${INPUT_RADIUS}px`,
          backgroundColor: COMPONENT.inputField.bg.default,
          ...NOTCHED_OUTLINE_STYLE,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.hover,
          },
          "&.Mui-focused": FOCUS_RING_STYLE,
          ...ERROR_OUTLINE_STYLE,
          "&.Mui-disabled": {
            backgroundColor: COMPONENT.inputField.bg.disabled,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.disabled,
            },
          },
        }),
        input: {
          padding: `0 ${INPUT_PADDING_X}px`,
          fontSize: `${INPUT_FONT_SIZE}px`,
          "&::placeholder": {
            fontSize: `${INPUT_FONT_SIZE}px`,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: ownerState?.style?.height ?? INPUT_FIELD_HEIGHT,
          display: "flex",
          alignItems: "center",
          borderRadius: `${INPUT_RADIUS}px`,
          backgroundColor: COMPONENT.inputField.bg.default,
          ...NOTCHED_OUTLINE_STYLE,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.hover,
          },
          "&.Mui-focused": FOCUS_RING_STYLE,
          ...ERROR_OUTLINE_STYLE,
          "&.Mui-disabled": {
            backgroundColor: COMPONENT.inputField.bg.disabled,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.disabled,
            },
          },
        }),
        input: {
          padding: `0 ${INPUT_PADDING_X}px`,
          fontSize: `${INPUT_FONT_SIZE}px`,
          "&::placeholder": {
            fontSize: `${INPUT_FONT_SIZE}px`,
          },
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slotProps: {
          textField: {
            size: "small",
            slotProps: { inputLabel: { shrink: true } },
            sx: PICKER_FIELD_SX,
          },
        },
      },
    },
    MuiTimePicker: {
      defaultProps: {
        slotProps: {
          textField: {
            size: "small",
            slotProps: { inputLabel: { shrink: true } },
            sx: PICKER_FIELD_SX,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "h6" && {
            fontSize: "1.65rem",
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h5" && {
            fontSize: `${p.typography.fontSize.heading.h4}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h3" && {
            fontSize: `${p.typography.fontSize.title.t2}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h4" && {
            fontSize: `${p.typography.fontSize.title.t3}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "body2" && {
            fontWeight: p.typography.fontWeight.regular,
            fontSize: `${p.typography.fontSize.body.b3}px`,
          }),
          ...(ownerState.variant === "subtitle2" && {
            fontSize: `${p.typography.fontSize.body.b4}px`,
            fontWeight: p.typography.fontWeight.regular,
            color: c.neutrals[2500] + "66",
          }),
          ...(ownerState.variant === "subtitle1" && {
            fontSize: `${p.typography.fontSize.body.b5}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "button" && {
            fontSize: `${p.typography.fontSize.body.b3}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, .05)"
              : "rgba(0, 0, 0, .03)",
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: `${p.typography.fontSize.body.b5}px !important`,
          textTransform: "none" as const,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "12px !important",
          borderRadius: ECARE_RADIUS.full,
          fontWeight: p.typography.fontWeight.medium,
          letterSpacing: "normal",
        },
        label: {
          fontSize: "12px",
        },
      },
    },
    MuiBadge: {
      defaultProps: {
        max: 9999,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: COMPONENT.card.bg,
          border: `1px solid ${ECARE_COLOR.border.default}`,
          borderRadius: COMPONENT.card.radius,
          // A resting card is separated by its border, not a shadow (§5).
          boxShadow: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: COMPONENT.modal.radius,
          // A modal floats, so the shadow carries it; a border here only shows
          // as a pale line around a coloured dialog header.
          boxShadow: ECARE_SHADOW.xl,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: ECARE_RADIUS.md,
          border: `1px solid ${ECARE_COLOR.border.default}`,
          boxShadow: ECARE_SHADOW.lg,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: ECARE_COLOR.text.primary,
          borderRadius: ECARE_RADIUS.md,
          fontSize: `${p.typography.fontSize.body.b5}px`,
        },
      },
    },
  },
});

export default theme;
